import { IObservable } from '~/rage/utils';
import { ExchangeItemType, VehicleNumberPlate } from '~/shared';

/** Статус обмена */
export enum ExchangeState {
	/** Игроки выбирают предметы для обмена */
	PlacingItems = 'placing_items',
	/** Игроки подтверждают обмен (нельзя добавлять предметы) */
	ConfirmingExchange = 'confirming_exchange',
	/** Происходит обмен имущества */
	ProcessingItems = 'processing_items',
	/** Обмен завершен */
	Completed = 'completed',
}

export interface IExchangeItem {
	id: string;
	type: ExchangeItemType;
}

export interface IExchangeItemMoney extends IExchangeItem {
	type: ExchangeItemType.Money;
	amount: number;
}

export interface IExchangeItemHouse extends IExchangeItem {
	type: ExchangeItemType.House;
	houseId: string;
}

export interface IExchangeItemVehicle extends IExchangeItem {
	type: ExchangeItemType.Vehicle;
	vehicleId: string;
	vehicleName: string;
	model: string;
	numberPlate: VehicleNumberPlate | null;
}

export interface IExchangeItemNumberPlate extends IExchangeItem {
	type: ExchangeItemType.NumberPlate;
	numberPlate: VehicleNumberPlate;
}

export interface IExchangeCharacterData {
	id: string;
	name: string;
	/** Нажал ли игрок кнопку на экране со списком предметов */
	isConfirmedItems: boolean;
	/** Нажал ли игрок кнопку на экране подтверждения обмена */
	isConfirmedExchange: boolean;
	/** Предметы, которые игрок добавил в обмен */
	placedItems: IExchangeItem[];
}

/**
 * Состояние обмена, приходящее от бэка
 */
export interface IExchangeData {
	id: string;
	state: ExchangeState;
	/** Был ли обмен завершен с отменой */
	isCancelled: boolean;
	/** Сумма налога */
	tax: number;
	/** Состояние персонажей в обмене */
	characters: [IExchangeCharacterData, IExchangeCharacterData];
}

export interface IExchangeService {
	/** Поиск игроков для обмена */
	findPlayers(
		nickname?: string,
	): Promise<{ characterId: string; nickname: string }[]>;

	/** Пригласить игрока на обмен */
	invite(characterId: string): Promise<void>;
	/** Добавить предмет на обмен */
	placeItem(itemId: string, amount?: number): Promise<void>;
	/** Удалить предмет с обмена */
	removePlacedItem(itemId: string): Promise<void>;
	/** Подтвердить предметы */
	confirmItems(): Promise<void>;
	/** Подтвердить обмен */
	confirmExchange(): Promise<void>;
	/** Отменить текущий обмен */
	exitExchange(): Promise<void>;

	/** Нажал ли локальный игрок "Подтвердить обмен" на первом экране */
	isLocalPlayerConfirmedItems(): IObservable<boolean>;
	/** Нажал ли локальный игрок "Подтвердить обмен" на втором экране */
	isLocalPlayerConfirmedExchange(): IObservable<boolean>;

	/** Получить состояние, в котором находится обмен */
	getExchangeState(): IObservable<ExchangeState>;
	/** Получить список предметов, которые локальный игрок может выставить на обмен */
	getOwnedItems(): IObservable<IExchangeItem[]>;
	/** Получить состояние подтверждения предметов */
	getConfirmedItemsState(): IObservable<boolean>;

	/** Получить время задержки до разблокировки кнопки подтверждения обмена, в секундах */
	getExchangeDelaySec(): IObservable<number>;

	/** Получить список предметов, предложенных локальным игроком */
	getLocalPlayerPlacedItems(): IObservable<IExchangeItem[]>;
	/** Получить список предметов, предложенных другим игроком */
	getOtherPlayerPlacedItems(): IObservable<IExchangeItem[]>;

	/** Получить имя локального игрока */
	getLocalPlayerName(): IObservable<string | null>;
	/** Получить имя другого игрока */
	getOtherPlayerName(): IObservable<string | null>;

	/** Получить налог на текущую сделку */
	getExchangeTax(): IObservable<number>;

	isItemMoney(item: IExchangeItem): item is IExchangeItemMoney;
	isItemHouse(item: IExchangeItem): item is IExchangeItemHouse;
	isItemVehicle(item: IExchangeItem): item is IExchangeItemVehicle;
	isItemNumberPlate(item: IExchangeItem): item is IExchangeItemNumberPlate;
}
